import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { ShoppingCart, ImageOff, Loader2 } from 'lucide-react';
import { AppDispatch, RootState } from '../../../store/store';
import { addToCart, selectCartLoading } from '../../../store/slices/cartSlice';

interface ProductCardProps {
  id: string;
  name: string;
  imageUrl: string;
  price: number;
  originalPrice?: number;
  discountPercentage?: number;
  isFeatured?: boolean;
  isBestSeller?: boolean;
  isOutOfStock?: boolean;
  defaultVariantId?: string;
  hasMultipleVariants?: boolean;
  hasOptionGroups?: boolean;
  /** Default option-value ids (first value per group) for one-click add. */
  defaultOptionValueIds?: string[];
}

export default function ProductCard({
  id,
  name,
  imageUrl,
  price,
  originalPrice,
  discountPercentage,
  isOutOfStock = false,
  defaultVariantId,
  hasMultipleVariants = false,
  hasOptionGroups = false,
  defaultOptionValueIds,
}: ProductCardProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const cartLoading = useSelector(selectCartLoading);

  const handleAddToCart = async (e: React.MouseEvent) => {
    // Prevent the surrounding link from navigating to the product page.
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/login', { state: { from: `/product/${id}` } });
      return;
    }

    if (!defaultVariantId) {
      // No purchasable variant resolved - let the user pick on the product page
      navigate(`/product/${id}`);
      return;
    }

    // If the product has option groups but we couldn't resolve their defaults
    // (e.g. options weren't loaded), send the user to the product page to choose.
    if (hasOptionGroups && (!defaultOptionValueIds || defaultOptionValueIds.length === 0)) {
      navigate(`/product/${id}`);
      return;
    }

    setIsAdding(true);
    try {
      await dispatch(addToCart({
        productVariantId: defaultVariantId,
        quantity: 1,
        // Include the default option selections (first value per group) when present.
        ...(defaultOptionValueIds && defaultOptionValueIds.length > 0
          ? { selectedOptionValueIds: defaultOptionValueIds }
          : {}),
      })).unwrap();
      toast.success('Added to cart!', { description: name });
    } catch (error: any) {
      toast.error(error || 'Failed to add to cart');
    } finally {
      setIsAdding(false);
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  return (
    <Link
      to={`/product/${id}`}
      className="storefront-card cursor-pointer group relative transition-all duration-300 ease-out shadow-sm hover:shadow-xl hover:scale-[1.02] h-full flex flex-col"
    >
      {/* Discount Badge */}
      {!isOutOfStock && discountPercentage && discountPercentage > 0 && (
        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-md">
          -{discountPercentage}%
        </div>
      )}

      {/* Product Image */}
      <div className="relative aspect-square overflow-hidden bg-neutral-100">
        {imageLoading && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-neutral-200 animate-pulse">
            <div className="w-12 h-12 border-4 border-neutral-300 border-t-neutral-400 rounded-full animate-spin"></div>
          </div>
        )}

        {imageError || !imageUrl ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-neutral-100 text-neutral-500">
            <ImageOff className="h-12 w-12 mb-2" />
            <span className="text-sm font-medium">No Image</span>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
            loading="lazy"
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}

        {isOutOfStock && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <span className="text-lg font-bold text-neutral-800 bg-white px-4 py-2 rounded-md shadow-md">
              Out of Stock
            </span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-2 sm:p-4 flex-1 flex flex-col">
        <h3 className="text-sm sm:text-base font-medium text-neutral-900 mb-1 sm:mb-2 line-clamp-2 min-h-[2.5rem] sm:min-h-[3rem]">
          {name}
        </h3>

        <div className="flex items-center justify-between gap-2 min-h-[2rem] sm:min-h-[2.25rem] mt-auto">
          <div className="flex flex-wrap items-center gap-1 sm:gap-2 min-w-0">
            <span className={`text-sm sm:text-lg font-semibold ${isOutOfStock ? 'text-neutral-400' : 'text-brand-primary'}`}>
              {hasMultipleVariants && <span className="text-[10px] sm:text-xs font-medium text-neutral-500 mr-0.5">From </span>}
              ${price.toFixed(2)}
            </span>
            {!isOutOfStock && originalPrice && originalPrice > price && (
              <span className="text-[10px] sm:text-sm text-neutral-500 line-through">
                ${originalPrice.toFixed(2)}
              </span>
            )}
          </div>
          
          {!isOutOfStock ? (
            <button
              onClick={handleAddToCart}
              disabled={isAdding || cartLoading}
              className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-brand-primary text-white rounded-full hover:bg-brand-primary-hover active:scale-95 transition-all shadow-sm disabled:opacity-50"
              aria-label="Add to cart"
            >
              {isAdding ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ShoppingCart className="h-4 w-4" />
              )}
            </button>
          ) : (
            /* Placeholder to maintain consistent height */
            <div className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9" />
          )}
        </div>
      </div>
    </Link>
  );
}
